/**
 * Small, HTML-free Markdown model for Flux Agent replies and its spoken-text counterpart. Assistant
 * text is untrusted model output, so it is parsed into plain block/inline tokens that React renders
 * as text nodes; nothing here produces HTML strings. Only headings, paragraphs, bullet/numbered
 * lists, block quotes, fenced code, rules, bold, italic, inline code, and http(s)/mailto links are
 * recognised; everything else stays literal text.
 *
 * @module apps/web/src/lib/fluxAgentMarkdown
 */

/** An inline run inside a block. Links only ever carry http(s) or mailto targets. */
export type MarkdownInline =
  | {
      /** Plain text run; adjacent text runs are merged. */
      type: 'text';
      /** Literal text, including any Markdown syntax that was not recognised. */
      text: string;
    }
  | {
      /** Bold run from `**text**` or `__text__`. */
      type: 'strong';
      /** Text with the delimiters removed. */
      text: string;
    }
  | {
      /** Italic run from `*text*` or word-bounded `_text_`. */
      type: 'em';
      /** Text with the delimiters removed. */
      text: string;
    }
  | {
      /** Inline code run from single backticks. */
      type: 'code';
      /** Code text with the backticks removed; never parsed further. */
      text: string;
    }
  | {
      /** Link whose target passed {@link safeHref}. */
      type: 'link';
      /** Visible link label. */
      text: string;
      /** Trimmed http(s) or mailto URL. */
      href: string;
    };

/** A top-level Markdown block. */
export type MarkdownBlock =
  | {
      /** ATX heading (`#` to `######`). */
      type: 'heading';
      /** Heading depth from 1 to 6, the number of leading `#` characters. */
      level: number;
      /** Heading text with trailing `#` characters removed. */
      inline: MarkdownInline[];
    }
  | {
      /** Consecutive non-blank lines joined with single spaces. */
      type: 'paragraph';
      /** Paragraph content. */
      inline: MarkdownInline[];
    }
  | {
      /** Run of consecutive bullet or numbered items of the same kind. */
      type: 'list';
      /** True for `1.`/`1)` items, false for `-`, `*` or `+` bullets. */
      ordered: boolean;
      /** One inline run per item; indented continuation lines are joined onto their item. */
      items: MarkdownInline[][];
    }
  | {
      /** Consecutive `>` lines joined into one quote. */
      type: 'quote';
      /** Quoted content without the `>` markers. */
      inline: MarkdownInline[];
    }
  | {
      /** Fenced code block; an unclosed fence runs to the end of the input. */
      type: 'code';
      /** Raw lines between the fences, joined with newlines. */
      text: string;
    }
  | {
      /** Thematic break from three or more `-`, `*` or `_`. */
      type: 'rule';
    };

/** Opening or closing code fence (``` or ~~~). */
const FENCE = /^\s*(```|~~~)/;
/** ATX heading; captures the `#` run and the text without closing hashes. */
const HEADING = /^\s{0,3}(#{1,6})\s+(.*?)\s*#*\s*$/;
/** Bullet list item with up to three spaces of indent; captures the item text. */
const BULLET = /^\s{0,3}[-*+]\s+(.*)$/;
/** Numbered list item (1-3 digits, then `.` or `)`); captures the item text. */
const NUMBERED = /^\s{0,3}\d{1,3}[.)]\s+(.*)$/;
/** Block-quote line; captures the text after `>` and one optional space. */
const QUOTE = /^\s{0,3}>\s?(.*)$/;
/** Thematic break: three or more of the same `-`, `*` or `_`, optionally spaced. */
const RULE = /^\s{0,3}([-*_])(\s*\1){2,}\s*$/;
/**
 * Global inline matcher. Capture groups in order: inline code, strong, emphasis, link. Underscore
 * emphasis must not touch word characters so `snake_case` stays literal.
 */
const INLINE =
  /(`[^`\n]+`)|(\*\*[^*\n]+\*\*|__[^_\n]+__)|(\*[^*\s][^*\n]*\*|(?<![A-Za-z0-9])_[^_\s][^_\n]*_(?![A-Za-z0-9]))|(\[[^\]\n]+\]\([^)\s]+\))/g;

/** Accept only link targets that cannot execute script or navigate to unexpected schemes. */
export function safeHref(href: string): string | null {
  const trimmed = href.trim();
  return /^(https?:\/\/|mailto:)/i.test(trimmed) ? trimmed : null;
}

/** Split one line of text into inline tokens. Unsafe links degrade to their visible label. */
export function parseInline(text: string): MarkdownInline[] {
  const tokens: MarkdownInline[] = [];
  let last = 0;
  const push = (token: MarkdownInline) => {
    const previous = tokens[tokens.length - 1];
    if (token.type === 'text' && previous?.type === 'text') previous.text += token.text;
    else if (token.text) tokens.push(token);
  };
  for (const match of text.matchAll(INLINE)) {
    const index = match.index ?? 0;
    if (index > last) push({ type: 'text', text: text.slice(last, index) });
    const [whole, code, strong, em, link] = match;
    if (code) push({ type: 'code', text: code.slice(1, -1) });
    else if (strong) push({ type: 'strong', text: strong.slice(2, -2) });
    else if (em) push({ type: 'em', text: em.slice(1, -1) });
    else if (link) {
      const label = link.slice(1, link.indexOf(']('));
      const href = safeHref(link.slice(link.indexOf('](') + 2, -1));
      push(href ? { type: 'link', text: label, href } : { type: 'text', text: label });
    } else push({ type: 'text', text: whole });
    last = index + whole.length;
  }
  if (last < text.length) push({ type: 'text', text: text.slice(last) });
  return tokens;
}

/** Parse Markdown into blocks. Blank lines separate paragraphs; list items may wrap onto indented lines. */
export function parseMarkdown(markdown: string): MarkdownBlock[] {
  const lines = markdown.replace(/\r\n?/g, '\n').split('\n');
  const blocks: MarkdownBlock[] = [];
  let paragraph: string[] = [];

  const flushParagraph = () => {
    if (paragraph.length)
      blocks.push({ type: 'paragraph', inline: parseInline(paragraph.join(' ')) });
    paragraph = [];
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (FENCE.test(line)) {
      flushParagraph();
      const fence = line.trim().slice(0, 3);
      const code: string[] = [];
      while (++i < lines.length && !lines[i].trim().startsWith(fence)) code.push(lines[i]);
      blocks.push({ type: 'code', text: code.join('\n') });
      continue;
    }
    if (!line.trim()) {
      flushParagraph();
      continue;
    }
    if (RULE.test(line)) {
      flushParagraph();
      blocks.push({ type: 'rule' });
      continue;
    }
    const heading = HEADING.exec(line);
    if (heading) {
      flushParagraph();
      blocks.push({ type: 'heading', level: heading[1].length, inline: parseInline(heading[2]) });
      continue;
    }
    const listMatch = BULLET.exec(line) || NUMBERED.exec(line);
    if (listMatch) {
      flushParagraph();
      const ordered = !BULLET.test(line);
      const items: string[] = [listMatch[1]];
      while (i + 1 < lines.length) {
        const next = lines[i + 1];
        const item = ordered ? NUMBERED.exec(next) : BULLET.exec(next);
        if (item) items.push(item[1]);
        else if (/^\s{2,}\S/.test(next) && !FENCE.test(next))
          items[items.length - 1] += ` ${next.trim()}`;
        else break;
        i++;
      }
      blocks.push({ type: 'list', ordered, items: items.map(parseInline) });
      continue;
    }
    const quote = QUOTE.exec(line);
    if (quote) {
      flushParagraph();
      const quoted: string[] = [quote[1]];
      while (i + 1 < lines.length && QUOTE.test(lines[i + 1]))
        quoted.push(QUOTE.exec(lines[++i])![1]);
      blocks.push({ type: 'quote', inline: parseInline(quoted.join(' ')) });
      continue;
    }
    paragraph.push(line.trim());
  }
  flushParagraph();
  return blocks;
}

/** Concatenate the visible text of inline tokens, dropping formatting and link targets. */
function inlineToText(inline: MarkdownInline[]): string {
  return inline.map(token => token.text).join('');
}

/** Trim text and append a period unless it already ends in punctuation; empty stays empty. */
function sentence(text: string): string {
  const trimmed = text.trim();
  if (!trimmed) return '';
  return /[.!?:;…]$/.test(trimmed) ? trimmed : `${trimmed}.`;
}

/**
 * Convert a Markdown reply into plain text suitable for speech synthesis. Code blocks and rules
 * are dropped, list items and headings become their own sentences, and link targets are not read.
 */
export function markdownToSpeech(markdown: string): string {
  const parts: string[] = [];
  for (const block of parseMarkdown(markdown)) {
    switch (block.type) {
      case 'heading':
        parts.push(sentence(inlineToText(block.inline)));
        break;
      case 'paragraph':
      case 'quote':
        parts.push(inlineToText(block.inline).trim());
        break;
      case 'list':
        parts.push(...block.items.map(item => sentence(inlineToText(item))));
        break;
      default:
        break;
    }
  }
  return parts.filter(Boolean).join(' ').replace(/\s+/g, ' ').trim();
}

/**
 * Shorten speech text to at most `maxChars`, preferring to end at a sentence boundary in the
 * latter half of the allowance so playback does not stop mid-word.
 */
export function truncateForSpeech(text: string, maxChars = 2000): string {
  if (text.length <= maxChars) return text;
  const window = text.slice(0, maxChars);
  const boundary = Math.max(
    window.lastIndexOf('. '),
    window.lastIndexOf('! '),
    window.lastIndexOf('? ')
  );
  if (boundary >= maxChars / 2) return window.slice(0, boundary + 1);
  const space = window.lastIndexOf(' ');
  return `${window.slice(0, space > 0 ? space : maxChars - 1).trimEnd()}…`;
}
